import { IconButton, Tooltip } from '@mui/joy';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import downloadIcon from '../../../../../../assets/svg/downloadIconGray.svg';
import otherFileTypeIcon from '../../../../../../assets/svg/otherFileType.svg';
import pdfIcon from '../../../../../../assets/svg/report/messenger/messagePDF.svg';
import videoIcon from '../../../../../../assets/svg/report/messenger/messageVideo.svg';
import docIcon from '../../../../../../assets/svg/report/messenger/messageWord.svg';
import { DOC_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, VIDEO_FILE_TYPES } from '../../../../../../constants/fileTypeConstants';
import { DOC_MIME_TYPE, IMAGE_MIME_TYPES, PDF_MIME_TYPE, VIDEO_MIME_TYPES } from '../../../../../../constants/mimeTypeConstants';
import AttachmentPopUp from '../attachmentPopup';

import { axiosInstanceForPublicAPI } from '~/assets/utils/axiosInstance';
import { CardOverflow } from '~/components/ui/card';
import { Text } from '~/components/ui/text';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { FileMessageAttachment } from '~/interfaces/MessagesObject';

interface MessageAttachmentsProps {
  attachments: FileMessageAttachment[];
  isWhistleblower?: boolean;
}

const MessageAttachments = ({ attachments, isWhistleblower }: MessageAttachmentsProps) => {
  const [popupOpen, setPopupOpen] = useState<number>(-1);
  const { createToastNotification } = useToastNotificationContext();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const intl = useIntl();

  const userAccessRoles = useAppSelector(
    (state) => state.reportData.userAccessRoles,
  );

  const getIcon = (currfileType: string, url?: string) => {
    if (IMAGE_FILE_TYPES.includes(currfileType)) return url;
    if (IMAGE_MIME_TYPES.includes(currfileType)) return url;
    if (VIDEO_FILE_TYPES.includes(currfileType)) return videoIcon;
    if (VIDEO_MIME_TYPES.includes(currfileType)) return videoIcon;
    if (DOC_FILE_TYPES.includes(currfileType)) return docIcon;
    if (DOC_MIME_TYPE === currfileType) return docIcon;
    if (PDF_FILE_TYPE === currfileType) return pdfIcon;
    if (PDF_MIME_TYPE === currfileType) return pdfIcon;

    return otherFileTypeIcon;
  };

  const downloadAttachment = async (url: string, name: string) => {
    if (userAccessRoles.is_allowed_to_download_attachments || isWhistleblower) {
      setIsLoading(true);
      try {
        // Perform the GET request and await the response
        const response = await axiosInstanceForPublicAPI().get(url, { responseType: 'blob' });
  
        // Create blob link to download
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', name);
  
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentNode?.removeChild(link);
        
        createToastNotification('Attachment downloaded successfully.', 'success');
      } catch (err) {
        // Handle any errors that occurred during the download
        createToastNotification('Something went wrong: ' + (err as Error).message, 'error');
      } finally {
        // Ensure isLoading is set to false in both success and error cases
        setIsLoading(false);
      }
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'You are not permitted to download any attachments files on this report.',
        }),
        'info',
      );
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '10px',
      width: '100%',
      padding: '10px 0px',
    }}>
      {attachments.map((file, key) => {
        return (
          <div key={key}  className='flex flex-row items-center gap-2 border-2 border-slate-300 rounded-md px-4 py-2 w-72 min-w-72' >
            <CardOverflow bleed onClick={() => setPopupOpen(key)}>
              <img className='max-h-10 cursor-pointer'
                src={getIcon(file.file_type, file.url)}
                height={40}
                width={40}
                alt='File Type Icon'
              />
            </CardOverflow>
            <div>
              <Tooltip title={file?.original_file_name}>
                <Text level='body-md' className='text-ellipsis whitespace-nowrap overflow-hidden w-40' >{file.original_file_name}</Text>
              </Tooltip>
              <Tooltip title={file?.file_type}>
                <Text level='body-md' className='text-gray-600 text-ellipsis whitespace-nowrap overflow-hidden w-40 uppercase' >{file?.file_type}</Text>
              </Tooltip>
            </div>
            <IconButton
              disabled={isLoading} 
              onClick={() => downloadAttachment(file.url, file.original_file_name)}
            >
              <img src={downloadIcon} alt='download icon'/>
            </IconButton>
            {popupOpen === key && 
              <AttachmentPopUp
                handleClose={() => setPopupOpen(-1)}
                download={() => downloadAttachment(file.url, file.original_file_name)}
                fileType={file.file_type}
                url={file.url}
                fileName={file.original_file_name}
              /> 
            }
          </div>
        );
      })}
    </div>
  );
};

export default MessageAttachments;